/**
 * "Link variables": filling a template variable with a class link made for
 * each recipient instead of from a CSV column (the old app's Map Variables).
 *
 * The send carries `mapped_others: {variable: link type}` and `session_id`;
 * lambda-comms-processing (`utils/comms_handler.py process_event`) finds each
 * recipient's profile (by `user_profile_id`, else email, else mobile) and
 * writes the link into that variable:
 *   hobby_class       https://bhanzu.com/extraClass/hobby/?lead_id=<profile>
 *   master_class      https://join.bhanzu.com/mcms/v1/student/attend/?session_id=<session_id>&lead_id=<profile>
 *   orientation_link  https://join.bhanzu.com/webinar-link/<profile>
 *
 * Unlike the old popup: the session id belongs to the master class link
 * (the old Save wanted it for orientation links instead, so a lone
 * orientation link could never be saved, and master class links went out
 * with an empty `session_id=`), and there is no batch session option (the
 * lambda notes it is not used from bulk comms; it reads `others.lead_id`).
 */

export const NO_LINK = 'none'

/** The link types offered, with the values the comms lambda reads. */
export const LINK_TYPES = [
    { value: NO_LINK, label: 'None (from a column)' },
    { value: 'hobby_class', label: 'Hobby class link' },
    { value: 'master_class', label: 'Master class link' },
    { value: 'orientation_link', label: 'Orientation link' },
]

const LINK_VALUES = new Set(
    LINK_TYPES.map((t) => t.value).filter((v) => v !== NO_LINK)
)

/**
 * Whether an event can have linked variables: it has template variables and
 * an inline receiver (recipients from the file), as in the old app.
 */
export const canLinkVariables = (details) =>
    !!details?.hasInlineReceiver && (details?.variables?.length ?? 0) > 0

/**
 * The real mappings, as the send's `mapped_others`: only variables of the
 * current event (choices made for another event are dropped) and only a
 * known link type ("None" and anything else are left out).
 *
 * @param {object} mapping `{variable: link type}` as chosen on the page
 * @param {string[]} variables the current event's template variables
 * @returns {object}
 */
export const activeMappings = (mapping = {}, variables = []) =>
    Object.fromEntries(
        variables
            .filter((v) => LINK_VALUES.has(mapping[v]))
            .map((v) => [v, mapping[v]])
    )

/** True when a master class link is chosen, which needs a session id. */
export const needsSessionId = (mapped = {}) =>
    Object.values(mapped).includes('master_class')

/** Why the mappings cannot be sent, or ''. */
export const sessionIdError = (mapped, sessionId) =>
    needsSessionId(mapped) && !String(sessionId ?? '').trim()
        ? 'Enter the master class session ID: the master class link needs it.'
        : ''

/** The `session_id` to send: the trimmed id with a master class link, else ''. */
export const sessionIdToSend = (mapped, sessionId) =>
    needsSessionId(mapped) ? String(sessionId ?? '').trim() : ''
